import { MutableRefObject, useCallback } from 'react';
import { scaleLinear } from 'd3-scale';
import { select, selectAll } from 'd3-selection';
import { isFinite, isNil, round } from 'lodash';
import { useUnmount } from 'react-use';
import { FEET } from '@surfline/web-common';

import type { Units } from 'types/units';
import getUnitType from 'utils/getUnitType';
import { roundSurfHeight } from 'utils/roundUnits';

import type { WaveDistributionTimestep } from 'types/cameraInsights';

import styles from '../WaveDistributionGraph.module.scss';
import graphStyles from '../../../../Graphs.module.scss';

const GRAPH_LABEL_HEIGHT = 20;
const CORNER_RADIUS = 2;
const GRAPH_PAD_TOP = 5;
const GRAPH_PAD_LEFT = 10;
const TICKS_Y_AXIS = 4;

interface Props {
  canvasRef: MutableRefObject<HTMLDivElement | null>;
  data?: WaveDistributionTimestep[];
  graphHeight?: number;
  graphWidth?: number;
  units?: Pick<Units, 'waves'>;
}

const useWaveDistributionGraph = ({ canvasRef, data, graphWidth, graphHeight, units }: Props) => {
  const setupGraph = useCallback(() => {
    if (
      canvasRef?.current &&
      graphWidth &&
      graphHeight &&
      isFinite(graphWidth) &&
      isFinite(graphHeight)
    ) {
      selectAll(`.wave-distribution-graph-svg`).remove();

      const graphData = data;

      if (isNil(graphData)) return;

      // calculate even distribution of the groups
      const numBars = graphData?.length ?? 1;
      const groupWidth = (graphWidth - GRAPH_PAD_LEFT) / numBars;
      // calculate the width of the bars to position with the groups
      const resize = scaleLinear().domain([2, 16]).range([1, 10]);
      const barWidth = Math.min(resize(groupWidth), 80);
      const barPositionX = groupWidth / 2 - barWidth / 2;

      // Note: we use scaleLinear instead of scaleTime to handle DST shifts and our time data is always linear
      const xScale = scaleLinear()
        .range([0, graphWidth - groupWidth - GRAPH_PAD_LEFT])
        .domain([0, numBars - 1]);

      const maxTotalHeight = Math.max(
        ...(data?.flatMap((singleDay) => singleDay.waveCountUnsurfed + singleDay.waveCountSurfed) ??
          []),
      );
      const unit = units?.waves?.height ?? FEET;

      const yScale = scaleLinear()
        .range([0, graphHeight - GRAPH_LABEL_HEIGHT])
        .domain([0, maxTotalHeight]);

      const barHeight = (reading: WaveDistributionTimestep, isSurfed: boolean) => {
        const zero = unit?.toLowerCase() === 'm' ? 0.12 : 0.4;
        let maxHeight;
        if (isSurfed) {
          maxHeight = reading.waveCountSurfed <= zero ? zero : reading.waveCountSurfed;
        } else {
          const totalWaves = reading.waveCountUnsurfed + reading.waveCountSurfed;
          maxHeight = totalWaves <= zero ? zero : totalWaves;
        }
        return Math.abs(yScale(maxHeight));
      };

      const barYPosition = (reading: WaveDistributionTimestep, isSurfed: boolean) =>
        graphHeight - barHeight(reading, isSurfed) + CORNER_RADIUS + GRAPH_PAD_TOP;

      const getWaveHeightDisplay = (dataPoint: WaveDistributionTimestep) => {
        const unitType = getUnitType(unit)?.toLowerCase();
        return dataPoint?.waveHeight.min === dataPoint?.waveHeight.max
          ? `${roundSurfHeight(dataPoint?.waveHeight.max, unit)}${unitType}`
          : `${roundSurfHeight(dataPoint?.waveHeight.min, unit)} - ${roundSurfHeight(
              dataPoint?.waveHeight.max,
              unit,
            )}${unitType}`;
      };

      const canvas = select(canvasRef.current)
        .append('svg')
        .attr('height', graphHeight)
        .attr('width', graphWidth)
        .attr('class', 'wave-distribution-graph-svg');

      // X-AXIS DIVIDER LINE
      canvas
        .append('line')
        .attr('x1', 0)
        .attr('y1', graphHeight - GRAPH_LABEL_HEIGHT + GRAPH_PAD_TOP + 2)
        .attr('x2', graphWidth)
        .attr('y2', graphHeight - GRAPH_LABEL_HEIGHT + GRAPH_PAD_TOP + 2)
        .attr('class', graphStyles.xAxisDivider);

      // Y-AXIS DIVIDER LINES
      const yAxisGroup = canvas.append('g');
      Array.from({ length: TICKS_Y_AXIS }).forEach((_, i) => {
        const yPosition = (graphHeight / (TICKS_Y_AXIS + 1)) * i + GRAPH_PAD_TOP;

        yAxisGroup
          .append('line')
          .attr('x1', 12)
          .attr('y1', yPosition)
          .attr('x2', graphWidth)
          .attr('y2', yPosition)
          .attr('class', graphStyles.yAxisTick);

        yAxisGroup
          .append('text')
          .attr('x', 0)
          .attr('y', yPosition)
          .text(round(maxTotalHeight - (maxTotalHeight / TICKS_Y_AXIS) * i, 0))
          .attr('class', graphStyles.yAxisLabel);
      });

      // BAR GROUP contains 2 bars and x-axis label
      const barGroup = canvas
        .append('g')
        .selectAll('g')
        .data(graphData)
        .enter()
        .append('g')
        .attr(
          'transform',
          (d, index) => `translate(${xScale(index) + GRAPH_PAD_LEFT},${barYPosition(d, false)})`,
        );

      // BAR UNSURFED WAVE COUNT
      barGroup
        .append('path')
        .attr('d', (d) => {
          const height = barHeight(d, false);
          const width = barWidth;
          const x = barPositionX;
          const y = -GRAPH_LABEL_HEIGHT;
          const r = CORNER_RADIUS;
          return `
            M ${x}, ${y + height}
            v ${-height + r}
            a ${r},${r} 0 0,1 ${r},${-r}
            h ${width - 2 * r}
            a ${r},${r} 0 0,1 ${r},${r}
            v ${height - r}
            z
          `;
        })
        .attr('class', () => styles.barUnsurfed)
        .attr('data-testid', 'wave-distribution-bar-unsurfed');

      // BAR SURFED WAVE COUNT
      barGroup
        .append('path')
        .attr('d', (d) => {
          const height = barHeight(d, true);
          const width = barWidth;
          const x = barPositionX;
          const y = -GRAPH_LABEL_HEIGHT + (barHeight(d, false) - barHeight(d, true));
          const r = CORNER_RADIUS;
          return `
            M ${x}, ${y + height}
            v ${-height + r}
            a ${r},${r} 0 0,1 ${r},${-r}
            h ${width - 2 * r}
            a ${r},${r} 0 0,1 ${r},${r}
            v ${height - r}
            z
          `;
        })
        .attr('class', () => styles.barSurfed)
        .attr('data-testid', 'wave-distribution-bar-surfed');

      // BAR LABEL
      barGroup
        .append('text')
        .attr('x', groupWidth / 2)
        .attr('y', (d) => barHeight(d, false) - GRAPH_LABEL_HEIGHT / 2.5)
        .attr('width', groupWidth)
        .attr('text-anchor', 'middle')
        .attr('data-testid', 'wave-distribution-bar-text')
        .attr('class', graphStyles.xAxisLabel)
        .text((d) => getWaveHeightDisplay(d));
    }
  }, [canvasRef, data, graphHeight, graphWidth, units]);

  useUnmount(() => {
    selectAll('.wave-distribution-graph-svg').remove();
  });

  return { setupGraph };
};

export default useWaveDistributionGraph;
